// theme.js


export const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === "light"
      ? {
        primary: {
          main: "#1976d2", // 💙 main app color
          light: "#63a4ff",
          dark: "#004ba0",
        },
        secondary: {
          main: "#F29325", // 🟠 accent color
        },
        background: {
          default: "#f9f9f9",
          paper: "#ffffff",
        },
        text: {
          primary: "#000000",
          secondary: "#333333",
        },
      }
      : {
        primary: {
          main: "#90caf9", // 💙 light blue in dark mode
          light: "#bbdefb",
          dark: "#0d47a1",
        },
        secondary: {
          main: "#F29325", // 🟠 consistent accent
        },
        background: {
          default: "#121212",
          paper: "#1e1e1e",
        },
        text: {
          primary: "#ffffff",
          secondary: "#aaaaaa",
        },
      }),
  },

  typography: {
    fontFamily: "'Inter', 'Roboto', sans-serif",
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          colorScheme: mode,
        },
        body: {
          colorScheme: mode,
          transition: "all 0.3s ease",
          scrollbarWidth: "thin",
          scrollbarColor: mode === "dark"
            ? "rgba(255, 255, 255, 0.2) transparent"
            : "rgba(0, 0, 0, 0.2) transparent",
        },
        "*": {
          scrollbarWidth: "thin",
          scrollbarColor: mode === "dark"
            ? "rgba(255, 255, 255, 0.2) transparent"
            : "rgba(0, 0, 0, 0.2) transparent",
        },
        "*::-webkit-scrollbar": {
          width: "6px",
          height: "6px",
        },
        "*::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: mode === "dark"
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.2)",
          borderRadius: "10px",
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: mode === "dark"
            ? "rgba(255, 255, 255, 0.35)"
            : "rgba(0, 0, 0, 0.35)",
        },
        "*::-webkit-scrollbar-corner": {
          backgroundColor: "transparent",
        },
        ".content-body": {
          transition: "all 0.3s ease",
          lineHeight: 1.7,
          "& pre, & code": {
            fontFamily: "Courier New, monospace",
          },
          // Theme-aware Headings
          "& h1": {
            fontSize: "1.9em",
            fontWeight: "700",
            color: mode === "light" ? "#e65100" : "#ffb74d",
            borderBottom: `4px solid ${mode === "light" ? "#e65100" : "#ffb74d"}`,
            paddingBottom: "8px",
            marginBottom: "22px",
          },
          "& h2": {
            fontSize: "1.5em",
            fontWeight: "600",
            color: mode === "light" ? "#1565c0" : "#90caf9",
            borderLeft: `6px solid ${mode === "light" ? "#1565c0" : "#90caf9"}`,
            paddingLeft: "12px",
            marginTop: "35px",
            marginBottom: "15px",
          },
          "& h3": {
            fontSize: "1.2em",
            fontWeight: "600",
            color: mode === "light" ? "#37474f" : "#eceff1",
            marginTop: "30px",
            marginBottom: "12px",
          },
          // Theme-aware Tables
          "& table": {
            width: "100%",
            borderCollapse: "collapse",
            margin: "20px 0",
            fontSize: "16px",
            backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
            color: mode === "light" ? "#222222" : "#f5f5f5",
            border: `1px solid ${mode === "light" ? "#e0e0e0" : "#333333"}`,
          },
          "& th": {
            backgroundColor: mode === "light" ? "#1976d2" : "#0d47a1",
            color: "#ffffff",
            fontWeight: "600",
            textAlign: "left",
            padding: "12px 16px",
            border: `1px solid ${mode === "light" ? "#1565c0" : "#0a3c85"}`,
          },
          "& td": {
            padding: "12px 16px",
            border: `1px solid ${mode === "light" ? "#e0e0e0" : "#333333"}`,
          },
          "& tr:nth-of-type(even)": {
            backgroundColor: mode === "light" ? "#f8fafd" : "#151515",
          },
          // Theme-aware Callouts
          "& .callout": {
            padding: "16px 20px",
            margin: "20px 0",
            borderRadius: "6px",
            borderLeft: "5px solid",
          },
          "& .callout.note": {
            backgroundColor: mode === "light" ? "#e3f2fd" : "#0d2035",
            borderLeftColor: "#1565c0",
            color: mode === "light" ? "#0d47a1" : "#90caf9",
          },
          "& .callout.warning": {
            backgroundColor: mode === "light" ? "#fff3e0" : "#35200d",
            borderLeftColor: "#ef6c00",
            color: mode === "light" ? "#e65100" : "#ffb74d",
          },
          "& .callout.danger": {
            backgroundColor: mode === "light" ? "#ffebee" : "#350d0d",
            borderLeftColor: "#c62828",
            color: mode === "light" ? "#b71c1c" : "#ef9a9a",
          },
          "& .callout.success": {
            backgroundColor: mode === "light" ? "#e8f5e9" : "#0d3514",
            borderLeftColor: "#2e7d32",
            color: mode === "light" ? "#1b5e20" : "#81c784",
          },
          // Theme-aware Dropdowns / Details
          "& details": {
            backgroundColor: mode === "light" ? "#fdfdfd" : "#1a1a1a",
            border: `1px solid ${mode === "light" ? "#e2e8f0" : "#2d3748"}`,
            borderRadius: "8px",
            margin: "15px 0",
            padding: "16px",
            transition: "all 0.2s ease",
          },
          "& details[open]": {
            borderColor: mode === "light" ? "#3182ce" : "#63b3ed",
          },
          "& summary": {
            fontWeight: "600",
            cursor: "pointer",
            color: mode === "light" ? "#2d3748" : "#e2e8f0",
            outline: "none",
            userSelect: "none",
          },
          "& summary:hover": {
            color: mode === "light" ? "#1976d2" : "#90caf9",
          },
          // Theme-aware Flowcharts / Diagrams
          "& .diagram-container": {
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "15px",
            backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
            border: `1px solid ${mode === "light" ? "#e0e0e0" : "#333333"}`,
            borderRadius: "8px",
            padding: "25px",
            margin: "25px 0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          },
          "& .diagram-card": {
            padding: "10px 15px",
            borderRadius: "6px",
            fontWeight: "600",
            color: "#ffffff",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          },
          "& .diagram-card.blue": { backgroundColor: "#1976d2" },
          "& .diagram-card.orange": { backgroundColor: "#e65100" },
          "& .diagram-card.green": { backgroundColor: "#2e7d32" },
          "& .arrow": {
            color: mode === "light" ? "#757575" : "#9e9e9e",
            fontWeight: "bold",
            fontSize: "18px",
          },
          // Theme-aware Horizontal Rules
          "& hr": {
            border: "none",
            height: "1px",
            background: mode === "light"
              ? "linear-gradient(90deg, transparent, #d0d0d0, transparent)"
              : "linear-gradient(90deg, transparent, #444, transparent)",
            margin: "36px 0",
          },
          // Theme-aware Lists
          "& ul, & ol": {
            paddingLeft: "24px",
            margin: "12px 0 18px",
          },
          "& li": {
            marginBottom: "8px",
            lineHeight: 1.7,
            color: mode === "light" ? "#333" : "#e0e0e0",
          },
          "& li::marker": {
            color: mode === "light" ? "#1976d2" : "#90caf9",
          },
          // Theme-aware Links
          "& a": {
            color: mode === "light" ? "#1565c0" : "#90caf9",
            textDecoration: "none",
            fontWeight: 600,
            borderBottom: `1px solid transparent`,
            transition: "border-color 0.2s, color 0.2s",
            "&:hover": {
              borderBottomColor: mode === "light" ? "#1565c0" : "#90caf9",
            },
          },
          // Theme-aware Strong/Bold text
          "& strong": {
            fontWeight: 700,
            color: mode === "light" ? "#222" : "#f5f5f5",
          },
          // Theme-aware Blockquotes
          "& blockquote": {
            borderLeft: `4px solid ${mode === "light" ? "#1976d2" : "#90caf9"}`,
            margin: "20px 0",
            padding: "12px 20px",
            backgroundColor: mode === "light" ? "#f5f8ff" : "rgba(144,202,249,0.06)",
            borderRadius: "0 8px 8px 0",
            fontStyle: "italic",
            color: mode === "light" ? "#444" : "#ccc",
          },
          // Theme-aware Inline Code (inside paragraphs)
          "& p code, & li code, & td code, & summary code": {
            backgroundColor: mode === "light" ? "#f0f4f8" : "rgba(255,255,255,0.1)",
            color: mode === "light" ? "#c62828" : "#ef9a9a",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "0.88em",
            fontFamily: "'Fira Code', 'Courier New', monospace",
            fontWeight: 500,
          },
          // Theme-aware Image Containers
          "& img": {
            maxWidth: "100%",
            height: "auto",
            borderRadius: "8px",
            margin: "16px 0",
            display: "block",
            boxShadow: mode === "light"
              ? "0 2px 12px rgba(0,0,0,0.08)"
              : "0 2px 12px rgba(0,0,0,0.3)",
          },
          // Theme-aware Paragraphs
          "& p": {
            marginBottom: "14px",
            lineHeight: 1.75,
            color: mode === "light" ? "#333" : "#e0e0e0",
          },
        },

        code: {
          fontFamily: "Courier New, monospace",
          borderRadius: "6px",
          padding: "2px 4px",
          backgroundColor: mode === "light" ? "#f4f4f4" : "#2d2d2d",
          color: mode === "light" ? "#111" : "#eee",
        },
        pre: {
          backgroundColor: mode === "light" ? "#f4f4f4" : "#1e1e1e",
          padding: "1rem",
          borderRadius: "8px",
          overflowX: "auto",
          borderLeft: `5px solid ${mode === "light" ? "#1e88e5" : "#90caf9"
            }`,
        },
        '[data-theme="dark"] body, [data-theme="dark"] .content-body': {
          backgroundColor: "#121212 !important",
          color: "#f5f5f5 !important",
        },
        '[data-theme="dark"] pre': {
          backgroundColor: "#1e1e1e !important",
          color: "#f5f5f5 !important",
        },
        '[data-theme="dark"] code': {
          color: "#90caf9 !important",
        },
        '[data-theme="light"] pre': {
          backgroundColor: "#f4f4f4 !important",
          color: "#222 !important",
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '&[aria-hidden="true"]': {
            visibility: "hidden !important",
            pointerEvents: "none !important",
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          '&[aria-hidden="true"]': {
            visibility: "hidden !important",
            pointerEvents: "none !important",
          },
        },
      },
    },
  },
});
